import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router';
import { Navigation, Pagination, Scrollbar, A11y } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import FadeInAnimation from '../../../utils/FadeInAnimation';
import { clickTracking } from '../../../utils/tracking';
import { getLanguageCode } from '../../../utils/utils';

import type { EditorialContent } from '@cobalt/utilities';
import { getWebContainerQuery } from '@queries/editorial';
import ErrorBoundary from '@shared/ErrorBoundary';

import * as styles from './styles.module.scss';

import 'swiper/scss';

const swiperBreakpoints = {
  0: {
    slidesPerView: 1.2,
    slidesPerGroup: 1,
    spaceBetween: 10,
  },
  440: {
    slidesPerView: 1.4,
    slidesPerGroup: 1,
    spaceBetween: 10,
  },
  768: {
    slidesPerView: 3.2,
    slidesPerGroup: 2,
    spaceBetween: 10,
  },
  1024: {
    slidesPerView: 4.2,
    slidesPerGroup: 3,
    spaceBetween: 10,
  },
  1200: {
    slidesPerView: 5.2,
    slidesPerGroup: 3,
    spaceBetween: 10,
  },
};

const handleClick = (d) => () => {
  if (!d.targetUrl) return;
  window.open(d.targetUrl, '_blank');
};

const DocumentsCarousel = ({
  containerName,
  documents: initalDocuments,
}: {
  containerName?: string;
  documents?: EditorialContent[];
}) => {
  const { locale } = useParams();
  const { data: documents } = useQuery({
    ...getWebContainerQuery(containerName, getLanguageCode(locale)),
    placeholderData: initalDocuments ?? [],
  });

  if (!documents?.length) return null;

  return (
    <ErrorBoundary>
      <Swiper
        className={styles.swiper}
        modules={[Navigation, Pagination, Scrollbar, A11y]}
        keyboard={false}
        freeMode={{
          sticky: true,
        }}
        breakpoints={swiperBreakpoints}
      >
        <meta itemProp='numberOfItems' content={`${documents.length}`} />
        {documents.map((d, i) => (
          <SwiperSlide key={`doc_${d.id}`} onClick={handleClick(d)}>
            <FadeInAnimation key={`doc_${d.id}`} delay={0 * i}>
              <div
                itemProp='itemListElement'
                itemScope
                itemType='https://schema.org/ListItem'
                className={
                  d.targetUrl
                    ? `${styles.card__content} js-tracking-link`
                    : `${styles.card__content} ${styles.card__coming}`
                }
                data-tracking={JSON.stringify({
                  ...clickTracking,
                  action: `Incredibles card - ${d.title}`,
                  more: { pdfTitle: d.title, editorialID: d.id },
                })}
                title={d.title}
              >
                <meta itemProp='position' content={`${i}`} />
                {!d.targetUrl && <div className={`${styles.card__image} ${styles.cardImageDisabled}`} />}
                {!!d.mainMedia?.image && (
                  <div
                    className={styles.card__image}
                    style={{ backgroundImage: `url(${d.mainMedia.image.portrait.p1})` }}
                  />
                )}
                <div
                  className={styles.card__wrapper}
                  itemProp='item'
                  itemScope
                  itemType='https://schema.org/DigitalDocument'
                >
                  <meta itemProp='url' content={`${window.location.href}#${d.id}`} />
                  <meta itemProp='image' content={d.mainMedia.image.portrait.p1} />
                  <div itemProp='description' className={styles.card__label}>
                    {d.description}
                  </div>
                  <div itemProp='name' className={styles.card__title}>
                    {d.title}
                  </div>
                </div>
              </div>
            </FadeInAnimation>
          </SwiperSlide>
        ))}
      </Swiper>
    </ErrorBoundary>
  );
};

export default DocumentsCarousel;
